import api from './api';
import { Destination, ApiResponse } from '../types';

const mockDestinations: Destination[] = [
  {
    id: '1',
    name: 'Ubud, Bali',
    country: 'Indonesia',
    description: 'A spiritual haven surrounded by rice terraces and ancient temples.',
    image: 'https://via.placeholder.com/400x300',
    coordinates: { lat: -8.5069, lng: 115.2625 },
  },
  {
    id: '2',
    name: 'Rishikesh, India',
    country: 'India',
    description: 'The yoga capital of the world, nestled in the Himalayas.',
    image: 'https://via.placeholder.com/400x300',
    coordinates: { lat: 30.0869, lng: 78.2676 },
  },
  {
    id: '3',
    name: 'Tulum, Mexico',
    country: 'Mexico',
    description: 'Ancient Mayan ruins overlooking pristine Caribbean beaches.',
    image: 'https://via.placeholder.com/400x300',
    coordinates: { lat: 20.2114, lng: -87.4292 },
  },
  {
    id: '4',
    name: 'Sedona, Arizona',
    country: 'USA',
    description: 'Red rock formations and powerful energy vortexes.',
    image: 'https://via.placeholder.com/400x300',
    coordinates: { lat: 34.8697, lng: -111.7610 },
  },
];

export const destinationService = {
  async getDestinations(): Promise<ApiResponse<Destination[]>> {
    // Mock implementation - replace with actual API call when backend is ready
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          data: mockDestinations,
          message: 'Destinations fetched successfully',
          success: true,
        });
      }, 800);
    });
    
    // Actual implementation (uncomment when backend is ready):
    // const response = await api.get('/destinations');
    // return response.data;
  },

  // Paginated destinations for public use
  async getPaginatedDestinations(params: { page?: number; limit?: number } = {}): Promise<ApiResponse<{ data: Destination[]; pagination: any }>> {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    
    const response = await api.get(`/destination/popular?${queryParams.toString()}`);
    return response.data;
  },

  async getDestinationById(id: string): Promise<ApiResponse<Destination>> {
    // Mock implementation
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        const destination = mockDestinations.find(d => d.id === id);
        if (destination) {
          resolve({
            data: destination,
            message: 'Destination fetched successfully',
            success: true,
          });
        } else {
          reject(new Error('Destination not found'));
        }
      }, 600);
    });
    
    // Actual implementation (uncomment when backend is ready):
    // const response = await api.get(`/destinations/${id}`);
    // return response.data;
  },

  async getPopularDestinations(): Promise<ApiResponse<Destination[]>> {
    // Mock implementation
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          data: mockDestinations.slice(0, 6),
          message: 'Popular destinations fetched successfully',
          success: true,
        });
      }, 700);
    });
    
    // Actual implementation (uncomment when backend is ready):
    // const response = await api.get('/destinations/popular');
    // return response.data;
  },

  // Admin CRUD operations
  async getAllDestinations(params: { page?: number; limit?: number } = {}): Promise<ApiResponse<{ data: Destination[]; pagination: any }>> {
    // For now using real API call based on the Postman examples you showed
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    
    const response = await api.get(`/destination?${queryParams.toString()}`);
    return response.data;
  },

  async createDestination(data: { name: string; details: string; isPopular: boolean; media: string }): Promise<ApiResponse<Destination>> {
    const response = await api.post('/destination', data);
    return response.data;
  },

  async updateDestination(id: string, data: { name: string; details: string; isPopular: boolean; media: string }): Promise<ApiResponse<Destination>> {
    const response = await api.put(`/destination/${id}`, data);
    return response.data;
  },

  async deleteDestination(id: string): Promise<ApiResponse<void>> {
    const response = await api.delete(`/destination/${id}`);
    return response.data;
  },

  async togglePopularStatus(id: string): Promise<ApiResponse<Destination>> {
    const response = await api.patch(`/destination/${id}/toggle-popular`);
    return response.data;
  },
};
